import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, Subject, BehaviorSubject } from "rxjs";

export interface ActionStatusPayload {
  action: number;
  assignee: number;
  status: "approved" | "rejected";
}

@Injectable({
  providedIn: "root",
})
export class ComplianceService {
  private readonly reloadComplianceListSource = new Subject<void>();
  reloadComplianceList$ = this.reloadComplianceListSource.asObservable();
  constructor(private readonly http: HttpClient) {}
  public complianceModeSubject = new BehaviorSubject<boolean>(false);
  complianceMode$ = this.complianceModeSubject.asObservable();

  setcomplianceMode(isCompliance: boolean) {
    this.complianceModeSubject.next(isCompliance);
  }

  getcomplianceMode(): boolean {
    return this.complianceModeSubject.getValue();
  }

  triggerReload() {
    this.reloadComplianceListSource.next();
  }

  // ------------------ Compliance Documents ------------------
  getComplianceDocuments(params: any = {}): Observable<any> {
    return this.http.get("grc/compliance-documents/", { params });
  }

  getComplianceDocumentById(id: number): Observable<any> {
    return this.http.get(`grc/compliance-documents/${id}/`);
  }

  createComplianceDocument(payload: any): Observable<any> {
    return this.http.post("grc/compliance-documents/", payload);
  }

  updateComplianceDocument(id: number, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-documents/${id}/`, payload);
  }

  getDocumentPDF(id: number) {
    return this.http.get(`grc/compliance-documents/${id}/download_document/`, {
      responseType: "blob",
    });
  }

  // ------------------- Amendment (Special type of Compliance Document) ----------
  getCommplianceDocTypes() {
    return this.http.get("valuelists/?type=compliance_doc_type");
  }

  // ------------------ Compliance Clauses ------------------
  getComplianceClauses(params: any = {}): Observable<any> {
    return this.http.get("grc/compliance-clauses/", { params });
  }

  getComplianceClausesNames(): Observable<any> {
    return this.http.get("grc/compliance-clauses/names");
  }

  getComplianceClauseById(id: number): Observable<any> {
    return this.http.get(`grc/compliance-clauses/${id}/`);
  }

  deleteComplianceClause(id: number): Observable<any> {
    return this.http.delete(`grc/compliance-clauses/${id}/`);
  }

  createComplianceClause(payload: any): Observable<any> {
    return this.http.post("grc/compliance-clauses/", payload);
  }

  updateComplianceClause(id: number, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-clauses/${id}/`, payload);
  }

  getFilteredComplianceClauses(params: string): Observable<any> {
    return this.http.get(`grc/compliance-sections/clauses/${params}`);
  }

  getComplianceStatus() {
    return this.http.get("base_value/?type=compliance_obligation_status");
  }

  getobligationSystemIdList(): Observable<any> {
    return this.http.get("grc/compliance-clauses/system-id/");
  }

  // ------------------ Website Configurations ------------------
  getWebsiteConfigurationsNames(): Observable<any> {
    return this.http.get("grc/website-configurations/names/");
  }

  getWebsiteConfigurations(params: string = ""): Observable<any> {
    return this.http.get(`grc/website-configurations/${params}`);
  }

  getProject() {
    return this.http.get(`projects/`);
  }

  getWebsiteConfigurationById(id: number): Observable<any> {
    return this.http.get(`grc/website-configurations/${id}/`);
  }

  createWebsiteConfiguration(payload: any): Observable<any> {
    return this.http.post("grc/website-configurations/", payload);
  }

  updateWebsiteConfiguration(id: number, payload: any): Observable<any> {
    return this.http.patch(`grc/website-configurations/${id}/`, payload);
  }

  // ------------------ Project Compliance Documents+Clauses ------------------

  getProjectComplianceClauses(projectId: number) {
    return this.http.get(
      `grc/compliance-documents/project-clauses/?project_id=${projectId}`
    );
  }

  // ------------------ Regulators ------------------
  getRegulators(): Observable<any> {
    return this.http.get("grc/compliance-regulators/");
  }

  // ------------------ Compliance Tags ------------------
  getComplianceTags(): Observable<any> {
    return this.http.get("grc/compliance-tags/");
  }

  saveComplianceTags(tags: string[], available: string[]): Observable<any> {
    return this.http.post("grc/compliance-tags/", { tags, available });
  }

  // -------------------Compliance Risks-------------

  getRecommendedComplianceRisks(clauseId: number) {
    return this.http.get(
      `grc/compliance-clauses/${clauseId}/recommended-risks/`
    );
  }
  getComplianceRisks(clauseId: number) {
    return this.http.get(`grc/compliance-clause-risks/?clause_id=${clauseId}`);
  }

  selectComplianceRisks(payload: any) {
    return this.http.post("grc/compliance-clause-risks/select-risk/", payload);
  }

  getPerformerReviwerMapping(): Observable<any> {
    return this.http.get(`grc/compliance-performer-reviewer-master/`);
  }

  // ------------------ Controls ------------------
  getObligationControls(id: any): Observable<any> {
    return this.http.get(`grc/compliance-controls/${id}`);
  }

  getObligationControlsList(qparams: string): Observable<any> {
    return this.http.get(`grc/compliance-controls/${qparams}`);
  }

  getObligationControlTasks(compliance_clause: any): Observable<any> {
    return this.http.get(
      `grc/compliance-controls/tasks/?compliance_clause=${compliance_clause}`
    );
  }

  createObligationControl(payload: any): Observable<any> {
    return this.http.post("grc/compliance-controls/", payload);
  }

  updateObligationControl(controlId: number, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-controls/${controlId}/`, payload);
  }

  getAiroRepos(){
    return this.http.get("grc/compliance-controls/airo-repos");
  }

  // ------------------ Tasks ------------------
  getObligationControlTask(id: any): Observable<any> {
    return this.http.get(`grc/compliance-tasks/${id}/`);
  }

  createControlTask(payload: any): Observable<any> {
    return this.http.post("grc/compliance-tasks/", payload);
  }

  getTaskStatusOptions() {
    return this.http.get("valuelists/?type=compliance_task_status");
  }

  updateControlTask(id: any, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-tasks/${id}/`, payload);
  }

  // ------------------ Performer-Reviewer ------------------
  getPerformerReviewer(payload: any): Observable<any> {
    return this.http.get("grc/compliance-performer-reviewer/", payload);
  }

  getComplianceFrequencyOptions() {
    return this.http.get("valuelists/?type=compliance_frequency");
  }

  getControlTypes() {
    return this.http.get("valuelists/?type=compliance_control_type");
  }

  getSectionDetails(id: any): Observable<any> {
    return this.http.get(`grc/compliance-sections/${id}/`);
  }
  // ------------------ action-status ------------------
  /**
   * Updates the compliance task action status
   * @param payload - Object containing action ID, assignee, and new status
   */
  updateActionStatus(payload: ActionStatusPayload): Observable<any> {
    const url = "grc/compliance-task-action/action-status/";

    return this.http.patch<any>(url, payload);
  }

  getAdjacentClauses(
    clauseId: number,
    documentId: number,
    filterQuery: string
  ): Observable<any> {
    return this.http.get(
      `grc/compliance-clauses/navigation/?document=${documentId}&obligation_id=${clauseId}${filterQuery}`
    );
  }
}
